import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { html, nothing, type PropertyValues } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { fromEvent, takeUntil } from 'rxjs'

/**
 * Radio button for use within a radio group.
 * @deprecated Use `schmancy-filter-chip` inside `schmancy-chips` for small option sets.
 */
@customElement('schmancy-radio-button')
export class RadioButton extends HostAriaLabelMixin(SchmancyElement) {
	@property({ type: String }) value = ''
	@property({ type: Boolean, reflect: true }) checked = false
	@property({ type: Boolean }) disabled = false
	@property({ type: String }) name = ''
	/**
	 * The radio's accessible name. Left empty, the name is computed from
	 * everything slotted in, so a rich multi-line label names the control with
	 * its whole block of text — a screen reader reads all of it before saying
	 * "radio", and role+name addressing by the one line that identifies the
	 * option misses, because that line is only the name's first token. Set to
	 * the short line that names the choice; the rest stays visible as
	 * supporting copy.
	 */
	@property({ type: String }) label = ''
	/**
	 * Whether this radio is the group's tab stop. A radio group is one stop with
	 * arrow keys inside it, so the enclosing `<schmancy-radio-group>` grants this
	 * to the checked option alone; a radio standing on its own keeps it.
	 */
	@property({ type: Boolean }) tabbable = true

	@query('input') private control?: HTMLInputElement

	connectedCallback() {
		super.connectedCallback()
		fromEvent<MouseEvent>(this, 'click').pipe(takeUntil(this.disconnecting)).subscribe(this.handleClick)
	}

	override focus(options?: FocusOptions) {
		this.control?.focus(options)
	}

	override firstUpdated(changed: PropertyValues) {
		super.firstUpdated(changed)
		if (!import.meta.env.DEV) return
		// A child assigned to a slot this control does not offer is dropped by the
		// platform with nothing thrown and nothing drawn. The offered names come
		// off the rendered slots, so this can never disagree with what render() takes.
		const offered = new Set(Array.from(this.renderRoot.querySelectorAll('slot')).map(slot => slot.name))
		const dropped = Array.from(this.children).find(child => !offered.has(child.slot))
		if (!dropped) return
		console.warn(
			`[schmancy-radio-button] A child carrying slot="${dropped.slot}" was dropped — this control ` +
				'has one unnamed slot, so its label is whatever it wraps directly. Remove the slot ' +
				'attribute. `label` does not stand in for it: that property sets the accessible name ' +
				'and renders nothing.',
			dropped,
		)
	}

	private handleClick = () => {
		if (this.disabled) return
		this.dispatchEvent(
			new CustomEvent('radio-button-click', {
				detail: { value: this.value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	render() {
		return html`
			<label class="flex items-start gap-3 cursor-pointer">
				<input
					type="radio"
					class="h-4 w-4 my-1 shrink-0 text-primary-default focus:ring-primary-container border-outline"
					aria-label=${this.ariaLabel || this.label || nothing}
					tabindex=${this.tabbable ? 0 : -1}
					.value=${this.value}
					.checked=${this.checked}
					.disabled=${this.disabled}
					.name=${this.name}
					@change=${() => {}}
				/>
				<slot></slot>
			</label>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-radio-button': RadioButton
	}
}
